import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Navigate } from 'react-router-dom';

import { api } from '../../../core/network/api';
import { queryClient } from '../../../core/network/query-client';
import { broadcastLogout } from '../../../core/network/session';
import { STORAGE_KEYS } from '../../../core/constants/storage';

// TSS vite/05 — Authentication context for the PUBLIC client channel
// (`/api/v1/public`, contract: .contract/api-reference/public-client-channel.md).
//
// Cookie+CSRF transport: the client JWT (signed with the client-only secret,
// isolated from staff) lives in the httpOnly `client_auth_token` cookie set by
// the backend on POST public/auth/login. JS never sees it. `client_csrf_token`
// (JS-readable) is echoed by the api interceptor in X-CSRF-Token on mutating
// requests. The client-namespaced cookie names ([0073]/Q-197) keep the portal
// from clobbering the staff dashboard's session on the same host.
//
// This file owns: session state, the client's grants, permission refresh,
// route guards, and the <Gate> component.

const EMPTY_SET = new Set();
const AuthContext = createContext(null);

// The public permissions block is `{ role, superuser, grants }` where grants
// are wildcard patterns as-is (e.g. `public:*`). Accept either the bare block
// or a payload that nests it under `.permissions` (the /me shape).
function readPermissionBlock(payload) {
    if (!payload) return {};
    if (Array.isArray(payload.grants)) return payload;
    if (payload.permissions) return payload.permissions;
    return {};
}

// grants carry wildcards; match them instead of exact-comparing.
// `*` → superuser; `public:*` → any `public:<x>`; else exact.
function grantMatches(pattern, domain) {
    if (pattern === '*') return true;
    if (pattern === domain) return true;
    if (pattern.endsWith(':*')) return domain.startsWith(pattern.slice(0, -1));
    return false;
}

export function AuthProvider({ children }) {
    const [user, setUser]                 = useState(null);
    const [initializing, setInitializing] = useState(true);
    const [permissions, setPermissions]   = useState(EMPTY_SET);
    const [superuser, setSuperuser]       = useState(false);

    const refreshTimer = useRef(null);

    const applyPermissions = useCallback((payload) => {
        const block  = readPermissionBlock(payload);
        const grants = Array.isArray(block.grants) ? block.grants : [];
        setPermissions(new Set(grants));
        setSuperuser(!!block.superuser || grants.includes('*'));
    }, []);

    const refreshPermissions = useCallback(async () => {
        try {
            const perms = await api.get('public/permissions').then(r => r.data);
            applyPermissions(perms);
        } catch {
            // Silent — refresh on 403 is opportunistic.
        }
    }, [applyPermissions]);

    // Bootstrap: read public/me + public/permissions once at mount.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [me, perms] = await Promise.all([
                    api.get('public/me').then(r => r.data),
                    api.get('public/permissions').then(r => r.data),
                ]);
                if (cancelled) return;
                setUser(me);
                applyPermissions(perms);
            } catch {
                if (cancelled) return;
                setUser(null);
            } finally {
                if (!cancelled) setInitializing(false);
            }
        })();
        return () => { cancelled = true; };
    }, [applyPermissions]);

    // Refresh on window focus + on the auth:permissions-refresh signal
    // emitted by the api interceptor when a 403 lands.
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                clearTimeout(refreshTimer.current);
                refreshTimer.current = setTimeout(refreshPermissions, 250);
            }
        };
        const onRefresh = () => refreshPermissions();

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('auth:permissions-refresh', onRefresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('auth:permissions-refresh', onRefresh);
            clearTimeout(refreshTimer.current);
        };
    }, [refreshPermissions]);

    // Multi-tab logout sync — TSS vite/05 §"Logout-everywhere". The other tabs
    // also drop their query cache: it holds the client's data.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_KEYS.LOGOUT_SIGNAL) {
                setUser(null);
                setPermissions(EMPTY_SET);
                setSuperuser(false);
                queryClient.clear();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // POST public/auth/login → contract (public-client-channel.md): the response
    // is `{ client: <perfil de /me> }`; the profile carries its own `permissions`
    // block. The `?? r.data` tolerates an unwrapped legacy/mock shape.
    const login = useCallback(async (credentials) => {
        const client = await api.post('public/auth/login', credentials).then(r => r.data?.client ?? r.data);
        setUser(client);
        applyPermissions(client);
    }, [applyPermissions]);

    const logout = useCallback(async () => {
        // Always clear local state, even if the network call fails.
        try { await api.post('public/auth/logout'); } catch { /* ignore */ }
        setUser(null);
        setPermissions(EMPTY_SET);
        setSuperuser(false);
        queryClient.clear();   // TSS vite/05 §Logout [RIGID]: the cache holds client data
        broadcastLogout();
    }, []);

    // Replace the cached client after a self-service edit (PATCH public/me
    // returns the GET /me shape). Keeps the sidebar/header/home name in sync
    // without re-bootstrapping. Grants don't change on a name/phone edit, so
    // the derived permission Set is left as-is.
    const updateUser = useCallback((next) => {
        if (next) setUser(next);
    }, []);

    const can = useCallback((domain) => {
        if (!domain) return true;
        if (superuser) return true;
        for (const pattern of permissions) {
            if (grantMatches(pattern, domain)) return true;
        }
        return false;
    }, [permissions, superuser]);

    const canAny = useCallback((...domains) => {
        if (!domains.length) return true;
        return domains.some(d => can(d));
    }, [can]);

    const canAll = useCallback((...domains) => {
        if (!domains.length) return true;
        return domains.every(d => can(d));
    }, [can]);

    const value = useMemo(() => ({
        user,
        isAuthenticated: !!user,
        initializing,
        permissions,
        superuser,
        can, canAny, canAll,
        refreshPermissions,
        login, logout, updateUser,
    }), [user, initializing, permissions, superuser, can, canAny, canAll, refreshPermissions, login, logout, updateUser]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}

// <Gate> — TSS vite/05 §"UI gating — <Gate> only".
// The ONLY way to conditional-render JSX by permission. Inline can() in
// JSX is forbidden; use can/canAny/canAll only outside JSX.
export function Gate({ domain, any, all, fallback = null, children }) {
    const { can, canAny, canAll } = useAuth();

    let allowed = true;
    if (domain) allowed = can(domain);
    else if (any && any.length) allowed = canAny(...any);
    else if (all && all.length) allowed = canAll(...all);

    return allowed ? <>{children}</> : <>{fallback}</>;
}

// Route guards — TSS vite/05 §"Route guards".

export function ProtectedRoute({ children, domain, any }) {
    const { isAuthenticated, initializing, can, canAny } = useAuth();

    if (initializing) return <BootLoader />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (domain && !can(domain)) return <Navigate to="/unauthorized" replace />;
    if (any && any.length && !canAny(...any)) return <Navigate to="/unauthorized" replace />;
    return children;
}

export function AuthRoute({ children }) {
    const { isAuthenticated, initializing } = useAuth();
    if (initializing) return <BootLoader />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    return children;
}

function BootLoader() {
    return (
        <div className="boot-loader">
            <div className="boot-loader__spinner" />
            <span className="boot-loader__text">Verificando sesión...</span>
        </div>
    );
}
